import { useState } from "react";
import { ArrowUpRight, CloudArrowUp, Code, LockKey, Plug, PuzzlePiece, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const Box = ({ title, children, testId }) => (
  <div className="rounded-2xl border border-teal-500/15 bg-black/20 p-4" data-testid={testId}>
    <h4 className="text-sm font-semibold text-white">{title}</h4>
    <div className="mt-3">{children}</div>
  </div>
);

export const ConnectorVaultPanel = ({ data, onRefresh }) => {
  const [form, setForm] = useState({ connector_name: "Salesforce", auth_type: "oauth2", base_url: "", client_id: "", client_secret: "", scopes: "", tenant_id: "", notes: "" });
  const catalog = data?.catalog || [];
  const saved = data?.saved || [];
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });
  const save = async () => {
    try {
      await api.post("/ekam/connectors", form);
      toast.success("Encrypted connector profile saved.");
      setForm({ ...form, client_secret: "" });
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save connector.");
    }
  };
  return (
    <div className="space-y-4" data-testid="connector-vault-panel">
      <Box title="Add BYOK connector" testId="connector-form">
        <select value={form.connector_name} onChange={set("connector_name")} data-testid="connector-select" className="h-11 w-full rounded-xl border border-teal-500/15 bg-[#041718] px-3 text-sm text-white">
          {catalog.map((item) => <option key={item.id} value={item.name}>{item.name} — {item.category}</option>)}
        </select>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <Input value={form.auth_type} onChange={set("auth_type")} data-testid="connector-auth-type" placeholder="oauth2 / api_key" className="h-10 border-teal-500/15 bg-[#041718] text-white" />
          <Input value={form.tenant_id} onChange={set("tenant_id")} data-testid="connector-tenant-id" placeholder="Tenant ID" className="h-10 border-teal-500/15 bg-[#041718] text-white" />
        </div>
        <Input value={form.base_url} onChange={set("base_url")} data-testid="connector-base-url" placeholder="Base URL" className="mt-2 h-10 border-teal-500/15 bg-[#041718] text-white" />
        <Input value={form.client_id} onChange={set("client_id")} data-testid="connector-client-id" placeholder="Client ID" className="mt-2 h-10 border-teal-500/15 bg-[#041718] text-white" />
        <Input type="password" value={form.client_secret} onChange={set("client_secret")} data-testid="connector-secret-input" placeholder="Client secret / token / API key" className="mt-2 h-10 border-teal-500/15 bg-[#041718] text-white" />
        <Button onClick={save} data-testid="connector-save-btn" className="mt-3 w-full rounded-full bg-cyan-400 text-slate-950"><LockKey size={15} className="mr-2" /> Save encrypted</Button>
      </Box>
      <Box title={`Connected apps (${saved.length})`} testId="connector-saved-list">
        {saved.length === 0 ? <p className="text-xs text-slate-500">No connectors saved yet.</p> : saved.map((item) => (
          <div key={item.connector_profile_id} className="mb-2 rounded-xl bg-white/[0.03] p-3 text-xs text-slate-400">
            <div className="flex items-center justify-between gap-3"><strong className="text-white">{item.connector_name}</strong><button onClick={async () => { await api.delete(`/ekam/connectors/${item.connector_profile_id}`); onRefresh(); }} data-testid={`connector-delete-${item.connector_profile_id}`} className="text-red-300"><Trash size={14} /></button></div>
            <p className="mt-1">{item.auth_type} · {item.base_url || "Provider default"}</p>
          </div>
        ))}
      </Box>
    </div>
  );
};

export const PluginMarketplacePanel = ({ data, onRefresh }) => (
  <div className="space-y-3" data-testid="plugin-marketplace-panel">
    {(data?.plugins || []).map((plugin) => (
      <div key={plugin.id} className="rounded-2xl border border-teal-500/10 bg-black/20 p-4">
        <div className="flex items-start justify-between gap-3">
          <div><p className="text-sm font-semibold text-white">{plugin.name}</p><p className="mt-1 text-xs text-slate-500">{plugin.category}</p></div>
          <PuzzlePiece size={18} className="text-cyan-300" />
        </div>
        <p className="mt-3 text-xs leading-relaxed text-slate-400">{plugin.description}</p>
        <Button disabled={plugin.installed} onClick={async () => { await api.post(`/ekam/plugins/${plugin.id}/install`); toast.success("Plugin installed."); onRefresh(); }} data-testid={`plugin-install-${plugin.id}`} className="mt-4 w-full rounded-full bg-cyan-400 text-slate-950 disabled:bg-emerald-500/20 disabled:text-emerald-200">
          {plugin.installed ? "Installed" : "Install"}
        </Button>
      </div>
    ))}
  </div>
);

export const ImageLibraryPanel = ({ data, onRefresh }) => {
  const [file, setFile] = useState(null);
  const upload = async () => {
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    await api.post("/ekam/images", form, { headers: { "Content-Type": "multipart/form-data" } });
    setFile(null);
    toast.success("Image uploaded.");
    onRefresh();
  };
  return (
    <div className="space-y-4" data-testid="image-library-panel">
      <label className="flex cursor-pointer flex-col items-center justify-center rounded-2xl border border-dashed border-cyan-400/30 bg-cyan-400/5 p-5 text-center">
        <CloudArrowUp size={24} className="text-cyan-300" />
        <span className="mt-2 text-xs text-slate-400">{file ? file.name : "Upload image"}</span>
        <input type="file" accept="image/*" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="image-upload-input" />
      </label>
      <Button onClick={upload} disabled={!file} data-testid="image-upload-btn" className="w-full rounded-full bg-cyan-400 text-slate-950">Upload</Button>
      <div className="grid grid-cols-2 gap-2">
        {(data?.images || []).map((image) => (
          <div key={image.image_id} className="rounded-xl border border-teal-500/10 bg-black/20 p-2">
            <img src={`${process.env.REACT_APP_BACKEND_URL}/api/ekam/images/${image.image_id}`} alt={image.filename} className="aspect-square w-full rounded-lg object-cover" />
            <p className="mt-2 truncate text-[10px] text-slate-500">{image.filename}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export const CodingWorkspacePanel = ({ data, onRefresh }) => {
  const [name, setName] = useState("Enterprise AI App");
  const [project, setProject] = useState(null);
  const [fileIndex, setFileIndex] = useState(0);
  const [answer, setAnswer] = useState("");
  const [busy, setBusy] = useState(false);
  const file = project?.files?.[fileIndex];
  const create = async () => {
    const { data: created } = await api.post("/ekam/code/projects", { name });
    const { data: full } = await api.get(`/ekam/code/projects/${created.code_project_id}`);
    setProject(full);
    setFileIndex(0);
    onRefresh();
  };
  const open = async (id) => {
    const { data: full } = await api.get(`/ekam/code/projects/${id}`);
    setProject(full);
    setFileIndex(0);
  };
  const save = async () => {
    await api.put(`/ekam/code/projects/${project.code_project_id}`, { files: project.files });
    toast.success("Project saved.");
  };
  const assist = async () => {
    setBusy(true);
    try {
      const { data: result } = await api.post("/ekam/code/assist", { project_id: project.code_project_id, file_name: file.name, language: file.language, content: file.content, prompt: "Review this code and suggest production improvements, tests and security controls." });
      setAnswer(result.answer);
      toast.success(`Coding assistant completed. ${result.credits_charged} credits charged.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Coding assist failed.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-4" data-testid="coding-workspace-panel">
      {!project ? (
        <>
          <div className="flex gap-2"><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="code-project-name" className="h-10 border-teal-500/15 bg-[#041718] text-white" /><Button onClick={create} data-testid="code-project-create" className="rounded-xl bg-cyan-400 text-slate-950"><Code size={16} /></Button></div>
          {(data?.projects || []).map((item) => <button key={item.code_project_id} onClick={() => open(item.code_project_id)} data-testid={`code-project-${item.code_project_id}`} className="w-full rounded-xl bg-white/[0.03] p-3 text-left text-sm text-slate-300">{item.name}</button>)}
        </>
      ) : (
        <>
          <div className="flex flex-wrap gap-2">{project.files.map((item, i) => <button key={item.name} onClick={() => setFileIndex(i)} className={`rounded-full px-3 py-1.5 text-xs ${i === fileIndex ? "bg-cyan-400 text-slate-950" : "bg-white/[0.04] text-slate-400"}`}>{item.name}</button>)}</div>
          <textarea value={file?.content || ""} onChange={(e) => setProject({ ...project, files: project.files.map((item, i) => i === fileIndex ? { ...item, content: e.target.value } : item) })} data-testid="code-editor" className="min-h-72 w-full rounded-2xl border border-teal-500/15 bg-black/30 p-4 font-mono text-xs leading-relaxed text-slate-200" />
          <div className="grid grid-cols-2 gap-2"><Button onClick={save} data-testid="code-save-btn" className="rounded-full bg-white text-slate-950">Save</Button><Button onClick={assist} disabled={busy} data-testid="code-assist-btn" className="rounded-full bg-cyan-400 text-slate-950">{busy ? "Running…" : "Ask Coding AI"}</Button></div>
          {answer && <pre className="max-h-80 overflow-auto whitespace-pre-wrap rounded-xl bg-black/30 p-3 text-xs text-slate-300" data-testid="code-assist-answer">{answer}</pre>}
        </>
      )}
    </div>
  );
};

export const GovernancePanel = ({ data }) => (
  <div className="space-y-4" data-testid="governance-panel">
    <div className="rounded-2xl border border-emerald-400/20 bg-emerald-400/5 p-4 text-sm text-emerald-100">System status: {data?.status?.status || "operational"} · Database: {data?.status?.database || "connected"}</div>
    {(data?.links || []).map((link) => <a key={link.name} href={link.url} target="_blank" rel="noreferrer" data-testid={`legal-${link.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="flex items-center justify-between rounded-xl border border-teal-500/10 bg-black/20 px-4 py-3 text-sm text-slate-300 hover:text-cyan-200">{link.name}<ArrowUpRight size={14} /></a>)}
    <p className="text-xs text-slate-500">{data?.footer || "© 2026 Anxya Tech Private Limited"}</p>
  </div>
);

export const EnterprisePanel = ({ activeNav, data, onRefresh }) => {
  if (activeNav === "Connectors") return <ConnectorVaultPanel data={data} onRefresh={onRefresh} />;
  if (activeNav === "Plugins") return <PluginMarketplacePanel data={data} onRefresh={onRefresh} />;
  if (activeNav === "Images") return <ImageLibraryPanel data={data} onRefresh={onRefresh} />;
  if (activeNav === "Coding") return <CodingWorkspacePanel data={data} onRefresh={onRefresh} />;
  if (activeNav === "Governance") return <GovernancePanel data={data} />;
  return null;
};
